import React, { useState, useEffect } from 'react';
import { Modal, Select, message } from 'antd';
import { CalendarOutlined, EnvironmentOutlined } from '@ant-design/icons';
import moment from 'moment';
import HeartLoader from '../../../../components/shared/HeartLoader';
import EventService from '../../../../services/EventService';
import BookingService from '../../../../services/BookingService';

const { Option } = Select;

const EventSelectionModal = ({ visible, onCancel, onSelect, patientName }) => {
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(null);

  useEffect(() => {
    if (visible) {
      loadEvents();
    }
  }, [visible]);

  const loadEvents = async () => {
    setLoading(true);
    try {
      const now = moment();

      // 1) Try to load upcoming bookings like the Bus Screening form does
      const todayStr = now.format('YYYY-MM-DD');
      let normalized = [];
      try {
        const bookingRes = await BookingService.getAllBookings({
          startDate: todayStr,
          status: 'confirmed',
          limit: 20
        });

        if (bookingRes?.success && bookingRes?.data?.bookings?.length) {
          const bookings = bookingRes.data.bookings;
          normalized = bookings.map((b) => {
            const title = b?.userInfo?.customData?.businessEventName || b?.eventType?.name || 'Event';
            const startDate = b?.bookingDate || b?.timeSlot?.startTime || b?.createdAt;
            const locationName = 'Mobile Unit Location';
            return {
              _id: b._id,
              title,
              startDate,
              // normalize to a string for simpler downstream usage/search
              location: locationName,
              _source: 'booking',
              raw: b
            };
          });
        }
      } catch (err) {
        // Non-fatal; fall back to events API below
        console.warn('Failed to load bookings for event selection; will fall back to Events API:', err);
      }

      // 2) If no bookings available, fall back to Events API
      if (!normalized.length) {
        const response = await EventService.getAllEvents();
        const allEvents = response?.data || [];

        normalized = allEvents.map((e) => ({
          _id: e._id,
          title: e.title,
          startDate: e.startDate || e.date,
          // make location a string for consistent rendering/search
          location: typeof e.location === 'string' ? e.location : (e.location?.name || ''),
          _source: 'event',
          raw: e
        }));
      }

      // Separate past and future using normalized startDate
      const past = normalized
        .filter((ev) => moment(ev.startDate).isBefore(now))
        .sort((a, b) => moment(b.startDate).diff(moment(a.startDate)))
        .slice(0, 10);

      const future = normalized
        .filter((ev) => moment(ev.startDate).isSameOrAfter(now))
        .sort((a, b) => moment(a.startDate).diff(moment(b.startDate)))
        .slice(0, 10);

      const filteredEvents = [...future, ...past];
      setEvents(filteredEvents);

      // Auto-select today's event if it exists
      const todayEvent = filteredEvents.find((ev) => moment(ev.startDate).isSame(now, 'day'));
      if (todayEvent) setSelectedEventId(todayEvent._id);
    } catch (error) {
      console.error('Error loading events:', error);
      message.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const handleOk = () => {
    if (!selectedEventId) {
      message.warning('Please select an event');
      return;
    }

    const selectedEvent = events.find(e => e._id === selectedEventId);
    onSelect(selectedEvent);
  };

  const formatEventLabel = (event) => {
    const date = moment(event.startDate).format('MM/DD/YYYY');
    // location may be string or object
    const location = typeof event.location === 'string' ? event.location : (event.location?.name || 'No location');
    const isToday = moment(event.startDate).isSame(moment(), 'day');
    const isFuture = moment(event.startDate).isAfter(moment());
    
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <CalendarOutlined style={{ color: isToday ? '#52c41a' : isFuture ? '#1890ff' : '#8c8c8c' }} />
        <span style={{ fontWeight: isToday ? 'bold' : 'normal' }}>
          {event.title}
        </span>
        <span style={{ color: '#8c8c8c' }}>•</span>
        <span style={{ color: '#595959' }}>{date}</span>
        <EnvironmentOutlined style={{ color: '#8c8c8c', marginLeft: '4px' }} />
        <span style={{ color: '#595959' }}>{location}</span>
        {isToday && <span style={{ color: '#52c41a', fontSize: '12px', marginLeft: '8px' }}>(Today)</span>}
      </div>
    );
  };

  return (
    <Modal
      title="Select Event for New Visit"
      open={visible}
      onCancel={onCancel}
      onOk={handleOk}
      okText="Create Visit"
      cancelText="Cancel"
      width={600}
      okButtonProps={{ disabled: !selectedEventId }}
    >
      <div style={{ marginBottom: '16px' }}>
        <p style={{ color: '#595959', marginBottom: '16px' }}>
          Creating a new visit for <strong>{patientName}</strong>. 
          Please select the event where this screening will take place.
        </p>
        
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <HeartLoader size="32px" text="Loading events..." padding="0" />
          </div>
        ) : (
          <Select
            style={{ width: '100%' }}
            placeholder="Select an event"
            value={selectedEventId}
            onChange={setSelectedEventId}
            size="large"
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) => {
              const event = events.find((e) => e._id === option?.value);
              if (!event) return false;
              const dateStr = moment(event.startDate).format('MM/DD/YYYY');
              const loc = typeof event.location === 'string' ? event.location : (event.location?.name || '');
              const searchStr = `${event.title} ${loc} ${dateStr}`.toLowerCase();
              return searchStr.includes((input || '').toLowerCase());
            }}
          >
            {events.map(event => (
              <Option key={event._id} value={event._id}>
                {formatEventLabel(event)}
              </Option>
            ))}
          </Select>
        )}

        {events.length === 0 && !loading && (
          <div style={{ textAlign: 'center', padding: '20px', color: '#8c8c8c' }}>
            No events available. Please create an event first.
          </div>
        )}
      </div>
    </Modal>
  );
};

export default EventSelectionModal;
